import type { MessageThread, ChatMessage, AuthorType, ThreadParticipant } from '@/types/messages';
const MAX_PER_THREAD = Number((import.meta as any).env?.VITE_MESSAGES_MAX_PER_THREAD ?? 1000);

const KEY_THREADS = 'coachd-message-threads';
const KEY_MESSAGES = 'coachd-messages'; // map of threadId -> ChatMessage[]

function readThreads(): MessageThread[] {
  try {
    const raw = localStorage.getItem(KEY_THREADS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeThreads(rows: MessageThread[]) {
  try {
    localStorage.setItem(KEY_THREADS, JSON.stringify(rows));
  } catch {
    // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
  }
}

function readMessagesMap(): Record<string, ChatMessage[]> {
  try {
    const raw = localStorage.getItem(KEY_MESSAGES);
    const parsed = JSON.parse(raw || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
function writeMessagesMap(map: Record<string, ChatMessage[]>) {
  try {
    localStorage.setItem(KEY_MESSAGES, JSON.stringify(map));
  } catch {
    // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
  }
}

function normalizeId(v: any): string {
  return String(v ?? '');
}

// BroadcastChannel for cross-tab sync
const bc: BroadcastChannel | null =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new (window as any).BroadcastChannel('coachd-messages')
    : null;
function notify(key: string, payload?: any) {
  try {
    bc?.postMessage({ key, payload });
  } catch {
    // OBS-11 a-ignore: BroadcastChannel is missing in some browsers and throws once closed; cross-tab sync is an enhancement, polling still refreshes.
  }
}

function recomputeUnread(thread: MessageThread, msgs: ChatMessage[]): { coach: number; client: number } {
  const coachId = normalizeId(thread.coachId);
  const clientId = normalizeId(thread.clientId);
  const coachLastRead =
    thread.participants.find((p) => p.userType === 'coach' && normalizeId(p.userId) === coachId)?.lastReadAt || '';
  const clientLastRead =
    thread.participants.find((p) => p.userType === 'client' && normalizeId(p.userId) === clientId)?.lastReadAt || '';
  const unreadForCoach = msgs.filter(
    (m) => m.authorType === 'client' && (!coachLastRead || m.createdAt > coachLastRead),
  ).length;
  const unreadForClient = msgs.filter(
    (m) => m.authorType === 'coach' && (!clientLastRead || m.createdAt > clientLastRead),
  ).length;
  return { coach: unreadForCoach, client: unreadForClient };
}

export const localMessagesStore = {
  getThread(id: string): Promise<MessageThread | undefined> {
    const rows = readThreads();
    return Promise.resolve(rows.find((t) => normalizeId(t.id) === normalizeId(id)));
  },
  getThreadByPair(clientId: string, coachId: string): MessageThread | null {
    const cid = normalizeId(clientId),
      pid = normalizeId(coachId);
    const threads = readThreads();
    if (!Array.isArray(threads)) return null;
    return threads.find((t) => normalizeId(t.clientId) === cid && normalizeId(t.coachId) === pid) || null;
  },
  async getThreadsByCoach(coachId: string): Promise<MessageThread[]> {
    const id = normalizeId(coachId);
    const threads = readThreads();
    if (!Array.isArray(threads)) return [];
    return threads.filter((t) => normalizeId(t.coachId) === id);
  },
  async getThreadsByClient(clientId: string): Promise<MessageThread[]> {
    const id = normalizeId(clientId);
    const threads = readThreads();
    if (!Array.isArray(threads)) return [];
    return threads.filter((t) => normalizeId(t.clientId) === id);
  },
  async createThread(clientId: string, coachId: string): Promise<MessageThread> {
    const existing = this.getThreadByPair(clientId, coachId);
    if (existing) return existing;
    const now = new Date().toISOString();
    const t: MessageThread = {
      id: `thread_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      clientId: normalizeId(clientId),
      coachId: normalizeId(coachId),
      participants: [
        { userType: 'client', userId: normalizeId(clientId), lastReadAt: now },
        { userType: 'coach', userId: normalizeId(coachId), lastReadAt: now },
      ],
      lastMessageAt: now,
      unreadForCoach: 0,
      unreadForClient: 0,
    };
    const rows = readThreads();
    rows.unshift(t);
    writeThreads(rows);
    const map = readMessagesMap();
    map[t.id] = [];
    writeMessagesMap(map);
    return t;
  },
  async appendMessage(msg: ChatMessage): Promise<void> {
    const map = readMessagesMap();
    const threadId = normalizeId(msg.threadId);
    const arr = map[threadId] || (map[threadId] = []);
    const stored = {
      ...msg,
      id: msg.id || `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    } as ChatMessage;
    arr.push(stored);
    // Trim oversized logs
    if (arr.length > MAX_PER_THREAD) {
      arr.splice(0, arr.length - MAX_PER_THREAD);
    }
    writeMessagesMap(map);
    notify(KEY_MESSAGES, { threadId });
    const threads = readThreads();
    const tIdx = threads.findIndex((t) => normalizeId(t.id) === threadId);
    if (tIdx >= 0) {
      const t = { ...threads[tIdx] } as MessageThread;
      t.lastMessageAt = msg.createdAt;
      const counts = recomputeUnread(t, map[threadId]);
      t.unreadForCoach = counts.coach;
      t.unreadForClient = counts.client;
      threads[tIdx] = t;
      writeThreads(threads);
      notify(KEY_THREADS);
    }
  },
  async markRead(threadId: string, userType: AuthorType, userId: string, atISO?: string): Promise<void> {
    const tid = normalizeId(threadId);
    const threads = readThreads();
    const idx = threads.findIndex((t) => normalizeId(t.id) === tid);
    if (idx < 0) return;
    const t = { ...threads[idx] } as MessageThread;
    const when = atISO || new Date().toISOString();
    t.participants = (t.participants || []).map((p: ThreadParticipant) => {
      if (p.userType === userType && normalizeId(p.userId) === normalizeId(userId)) return { ...p, lastReadAt: when };
      return p;
    });
    const map = readMessagesMap();
    const counts = recomputeUnread(t, map[tid] || []);
    t.unreadForCoach = counts.coach;
    t.unreadForClient = counts.client;
    threads[idx] = t;
    writeThreads(threads);
    notify(KEY_THREADS);
  },
  async getUnreadCounts(target: { coachId?: string; clientId?: string }): Promise<number> {
    const threads = readThreads();
    if (target.coachId) {
      const id = normalizeId(target.coachId);
      return threads.filter((t) => normalizeId(t.coachId) === id).reduce((sum, t) => sum + (t.unreadForCoach || 0), 0);
    }
    if (target.clientId) {
      const id = normalizeId(target.clientId);
      return threads
        .filter((t) => normalizeId(t.clientId) === id)
        .reduce((sum, t) => sum + (t.unreadForClient || 0), 0);
    }
    return 0;
  },
  async getMessages(threadId: string): Promise<ChatMessage[]> {
    const map = readMessagesMap();
    return (map[normalizeId(threadId)] || []).slice();
  },
};
